import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { loginLandingPath, returnPathAfterLogin } from "../utils/loginLanding";
import { saveSellerToken } from "../utils/sellerSession";
import { rateLimitMessage } from "../utils/rateLimit";
import { STORE_READY_KEY, ONBOARDING_TO_PRODUCTS_KEY } from "./StoreReady";
import AuthLayout from "./auth/AuthLayout";
import OtpBoxes from "./auth/OtpBoxes";
import { isMobileNumber, spacedPhone } from "./signup";

const OTP_LENGTH = 4;
const RESEND_SECONDS = 60;

function Login({ setIsLoggedIn }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [phone, setPhone] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [showOtpSection, setShowOtpSection] = useState(false);
  const [otp, setOtp] = useState("");
  const [otpError, setOtpError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);

  useEffect(() => {
    if (resendTimer <= 0) return undefined;
    const timer = setTimeout(() => setResendTimer(s => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendTimer]);

  const handleSendOtp = (e) => {
    e.preventDefault();
    if (!isMobileNumber(phone)) {
      setPhoneError(t("auth.invalid_phone"));
      return;
    }
    setPhoneError("");
    setIsSubmitting(true);

    axios
      .post(`${process.env.REACT_APP_APIURL}business/otplogin`, { phone_number: phone })
      .then((res) => {
        if (res.data.status === "success") {
          setOtp("");
          setOtpError("");
          setShowOtpSection(true);
          setResendTimer(RESEND_SECONDS);
          toast.success("OTP sent successfully");
        } else {
          toast.error(res.data.message || "Failed to send OTP");
        }
      })
      .catch((err) => {
        console.error("Login error:", err);
        toast.error(rateLimitMessage(err, t("auth.too_many")) || "Account not found. Please check your details or sign up.");
      })
      .finally(() => setIsSubmitting(false));
  };

  const verifyOTP = async (code) => {
    if (code.length !== OTP_LENGTH) {
      setOtpError(t("auth.otp_incomplete"));
      return;
    }
    setOtpError("");
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${process.env.REACT_APP_APIURL}business/verifyOtp`, { phone_number: phone, otp: code });

      if (response.data.status === "success") {
        window.localStorage.setItem("email_address", response.data.result.email_address);
        window.localStorage.setItem("user_id", response.data.result.user_id);
        window.localStorage.setItem("signup", response.data.result.signup);
        window.localStorage.setItem("phone_number", response.data.result.phone_number);
        if (response.data.result.signup === "user") {
          window.localStorage.setItem("business_id", response.data.result.business_id);
        }
        // Seller session for plan checkout and invoices
        saveSellerToken(response.data.result.seller_token);
        // An existing account: no Store Ready popup or onboarding story, even if
        // an earlier sign-up in this tab left their hand-off behind
        window.sessionStorage.removeItem(STORE_READY_KEY);
        window.sessionStorage.removeItem(ONBOARDING_TO_PRODUCTS_KEY);

        // Dispatch custom event to notify PlanContext to refresh
        window.dispatchEvent(new Event('userLoggedIn'));

        toast.success("Login successful!");
        // Checkout for a plan picked on mytamilkart.com, the plans page for a seller
        // who never bought one, else the products. Read now: /purchase-plan clears
        // the pending plan once it opens checkout
        // A page the login form stood in for (e.g. an order opened from a
        // notification) comes before the products
        const next = await loginLandingPath(response.data.result.business_id, returnPathAfterLogin() || "/gift");
        setIsLoggedIn(true);

        setTimeout(() => {
          navigate(next);
        }, 1000);
      } else {
        setOtpError("Invalid OTP. Please try again.");
      }
    } catch (error) {
      console.error("OTP verification error:", error);
      setOtpError(rateLimitMessage(error, t("auth.too_many")) || "Invalid OTP. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOtpChange = (value) => {
    setOtp(value);
    setOtpError("");
    if (value.length === OTP_LENGTH) verifyOTP(value);
  };

  const handleResendOTP = async (e) => {
    e.preventDefault();
    if (resendTimer > 0) return;
    try {
      await axios.post(`${process.env.REACT_APP_APIURL}business/resend-login-otp`, { phone_number: phone });
      toast.success('OTP has been resent');
      setOtp("");
      setResendTimer(RESEND_SECONDS);
    } catch (error) {
      console.error("Error resending OTP:", error);
      toast.error(rateLimitMessage(error, t("auth.too_many")) || "Failed to resend OTP. Please try again later.");
    }
  };

  return (
    <AuthLayout solo>
      {!showOtpSection ? (
        <form onSubmit={handleSendOtp} noValidate>
          <div className="fhead"><h1>{t("auth.business_login")}</h1></div>
          <div className="fld">
            <label htmlFor="sph">{t("auth.phone")} <span className="req" aria-hidden="true">*</span></label>
            <div className="inp">
              <span>+91</span>
              <input
                id="sph"
                inputMode="numeric"
                maxLength={10}
                autoComplete="tel-national"
                placeholder={t("auth.login_placeholder")}
                aria-describedby="sphH"
                value={phone}
                onChange={(e) => setPhone(e.target.value.replace(/\D/g, ""))}
              />
              <span className="ico" aria-hidden="true">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" aria-hidden="true"><circle cx="12" cy="8" r="4" /><path d="M4 21a8 8 0 0 1 16 0" /></svg>
              </span>
            </div>
            <span className="hint" id="sphH">{t("auth.login_hint")}</span>
          </div>
          <button className="go" type="submit" disabled={isSubmitting}>
            {isSubmitting ? t("auth.sending") : t("auth.get_otp")}
          </button>
          <p className="hint bad center" role="alert">{phoneError}</p>
          <p className="signin">{t("auth.no_account")} <a href="/signup">{t("auth.sign_up")}</a></p>
        </form>
      ) : (
        <form className="otpf" onSubmit={(e) => { e.preventDefault(); verifyOTP(otp); }} noValidate>
          <div className="fhead">
            <h1>{t("auth.enter_otp")}</h1>
            <p>{t("auth.sent_to", { phone: spacedPhone(phone) })}</p>
          </div>
          <OtpBoxes length={OTP_LENGTH} value={otp} onChange={handleOtpChange} />
          <p className="hint bad center" role="alert">{otpError}</p>
          <button className="go" type="submit" disabled={isSubmitting}>{t("auth.sign_in")}</button>
          <p className="signin">
            <a href="#sph" onClick={(e) => { e.preventDefault(); setShowOtpSection(false); }}>{t("auth.change_number")}</a>
            {" · "}
            <button type="button" onClick={handleResendOTP} disabled={resendTimer > 0}>
              {resendTimer > 0 ? t("auth.resend_in", { s: resendTimer }) : t("auth.resend")}
            </button>
          </p>
        </form>
      )}
    </AuthLayout>
  );
}

export default Login;
